import { useMsal } from '@azure/msal-react';
import { GraphError } from '@microsoft/microsoft-graph-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DocumentFile,
  DocumentItem,
} from '../features/Collaboration/SharedDocuments/DocumentFile';
import { getCurrentPrincipal } from '../lib/currentPrincipal';
import { sharedDocumentsClient } from '../utils/sharedDocuments.client';
import { usePrincipals } from './queries';

type SortableFields = keyof Pick<
  DocumentItem,
  'name' | 'lastModified' | 'owner'
>;

interface SortOptions {
  field: SortableFields;
  direction: 'asc' | 'desc';
}

const comparators: {
  [key in SortableFields]: (a: DocumentItem, b: DocumentItem) => number;
} = {
  name: (a, b) => a.name?.localeCompare(b.name ?? '') ?? 0,
  lastModified: (a, b) => {
    const timeA = a.lastModified?.getTime() ?? -Infinity;
    const timeB = b.lastModified?.getTime() ?? -Infinity;
    return timeA - timeB;
  },
  owner: (a, b) => a.owner?.localeCompare(b.owner ?? '') ?? 0,
};

export const useSharedDocuments = ({
  onUploadSuccess,
  onUploadError,
  onNoSharepointTeamError,
  onLoadError,
  onAccessDeniedError,
  maxResults,
  onDeleteSuccess,
  onDeleteError,
}: {
  onUploadSuccess?: () => void;
  onUploadError?: () => void;
  onNoSharepointTeamError?: () => void;
  onLoadError?: () => void;
  onAccessDeniedError?: () => void;
  maxResults?: number;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
} = {}) => {
  const { instance } = useMsal();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { data: principals } = usePrincipals();
  const queryClient = useQueryClient();

  const {
    currentPrincipal: { teamId },
  } = getCurrentPrincipal(principals);

  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortOptions>({
    field: 'name',
    direction: 'asc',
  });

  const {
    data: items,
    isLoading,
    error,
  } = useQuery<DocumentItem[]>({
    queryKey: ['sharedDocuments', teamId, location.search],
    queryFn: () => {
      if (!teamId) {
        throw new Error('Missing teams ID');
      }

      return sharedDocumentsClient.getFolderContents(
        teamId,
        params.get('path') ?? undefined,
        params.get('drive') ?? undefined,
      );
    },
    enabled: !!instance && !!teamId,
    gcTime: 0,
  });

  const { data: latestActivity, isLoading: isLatestActivityLoading } = useQuery<
    DocumentFile[]
  >({
    queryKey: ['latestActivity'],
    queryFn: () => {
      if (!instance || !teamId) {
        throw new Error('Missing instance or teams ID');
      }
      return sharedDocumentsClient.getLastModifiedFiles(teamId, maxResults);
    },
    enabled: !!instance && !!teamId,
    gcTime: 0,
  });

  useEffect(() => {
    if (!instance) {
      throw new Error('Missing instance');
    }
    if (!teamId && onNoSharepointTeamError) {
      return onNoSharepointTeamError();
    }
    if (error) {
      if (
        error instanceof GraphError &&
        error.statusCode === 403 &&
        onAccessDeniedError
      ) {
        return onAccessDeniedError();
      }
      if (onLoadError) {
        return onLoadError();
      }
    }
  }, [
    instance,
    teamId,
    error,
    onNoSharepointTeamError,
    onAccessDeniedError,
    onLoadError,
  ]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => {
      if (!instance || !teamId) {
        throw new Error('Missing instance or team ID');
      }
      setUploadProgress(0);
      return sharedDocumentsClient.uploadFile(
        file,
        teamId,
        params.get('path') ?? '',
        params.get('drive') ?? undefined,
        (percent) => setUploadProgress(percent),
      );
    },
    onSettled: () => setUploadProgress(null),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sharedDocuments', teamId, location.search],
      });
      queryClient.invalidateQueries({
        queryKey: ['latestActivity'],
      });
      onUploadSuccess?.();
    },
    onError: () => {
      onUploadError?.();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (file: DocumentItem) => {
      if (!instance || !teamId) {
        throw new Error('Missing instance or team ID');
      }
      if (!file.name) {
        throw new Error('File is missing a valid name');
      }

      return sharedDocumentsClient.deleteFile(
        file.name,
        teamId,
        params.get('path') ?? undefined,
        params.get('drive') ?? undefined,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['sharedDocuments', teamId, location.search],
      });
      queryClient.invalidateQueries({
        queryKey: ['latestActivity'],
      });
      onDeleteSuccess?.();
    },
    onError: () => {
      onDeleteError?.();
    },
  });

  const sortedItems = useMemo(() => {
    if (!items) return [];
    const sorted = [...items].sort(comparators[sortBy.field]);
    return sortBy.direction === 'asc' ? sorted : sorted.reverse();
  }, [items, sortBy]);

  return {
    sortedItems,
    isLoading,
    sortBy,
    setSortBy,
    uploadFile: uploadMutation.mutate,
    deleteFile: deleteMutation.mutate,
    uploadProgress,
    latestActivity,
    isLatestActivityLoading,
  };
};
